/* eslint-disable simple-import-sort/imports */
import { Global } from '@emotion/react';
import { Flex } from 'antd';
import ReactECharts from 'echarts-for-react';
import { PieChart, RadarChart } from 'echarts/charts';
import * as echarts from 'echarts/core';
import { CanvasRenderer } from 'echarts/renderers';
import { useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { CircularProgress } from '@/components/CircularProgress';
import FilledButton from '@/components/FilledButton';
import { SDGRadarChart } from '@/components/SDGRadarChart';
import { AnswerType, Industry, Path } from '@/enums';

echarts.use([RadarChart, PieChart, CanvasRenderer]);

import {
  exportToPDF,
  fetchQuestionnaire,
  getQuestionnaireFileUrl,
} from '@/apis';
import ImgLeaf from '@/assets/images/leaf.png';
import ImgLika from '@/assets/images/LIKA.png';
import Img1 from '@/assets/images/the-global-goals-grid-black-01.png';
import Img2 from '@/assets/images/the-global-goals-grid-black-02.png';
import Img3 from '@/assets/images/the-global-goals-grid-black-03.png';
import Img4 from '@/assets/images/the-global-goals-grid-black-04.png';
import Img5 from '@/assets/images/the-global-goals-grid-black-05.png';
import Img6 from '@/assets/images/the-global-goals-grid-black-06.png';
import Img7 from '@/assets/images/the-global-goals-grid-black-07.png';
import Img8 from '@/assets/images/the-global-goals-grid-black-08.png';
import Img9 from '@/assets/images/the-global-goals-grid-black-09.png';
import Img10 from '@/assets/images/the-global-goals-grid-black-10.png';
import Img11 from '@/assets/images/the-global-goals-grid-black-11.png';
import Img12 from '@/assets/images/the-global-goals-grid-black-12.png';
import Img13 from '@/assets/images/the-global-goals-grid-black-13.png';
import Img14 from '@/assets/images/the-global-goals-grid-black-14.png';
import Img15 from '@/assets/images/the-global-goals-grid-black-15.png';
import Img16 from '@/assets/images/the-global-goals-grid-black-16.png';
import Img17 from '@/assets/images/the-global-goals-grid-black-17.png';
import Img18 from '@/assets/images/the-global-goals-grid-black-18.png';
import { useLoading } from '@/contexts/LoadingContext';
import { useApiRequest, useBreakpoint } from '@/hooks';
import { useQuestionnaireScore } from '@/hooks/useQuestionnaireScore';
import {
  getIndustryResultAboutColors,
  getIndustryResultCircleColors,
} from '@/utils/industryColors';
import * as Style from './Result.style';

const sdgImg = [
  Img1,
  Img2,
  Img3,
  Img4,
  Img5,
  Img6,
  Img7,
  Img8,
  Img9,
  Img10,
  Img11,
  Img12,
  Img13,
  Img14,
  Img15,
  Img16,
  Img17,
];

export default function Result() {
  const { id } = useParams<{ id: string }>();
  const { withLoading } = useLoading();
  const navigate = useNavigate();
  const radarChartRef = useRef<ReactECharts>(null);
  const [uploadedImageUrl, setUploadedImageUrl] = useState<string | null>(null);

  // 獲取動態分數數據
  const {
    scoreData,
    resultData,
    loading,
    error,
    refetch,
    exportable,
    isGenerating,
  } = useQuestionnaireScore(id);

  const { isMobileOrTablet } = useBreakpoint();

  // Use useApiRequest to fetch uploaded questionnaire image with auto-cancellation
  useApiRequest(
    async () => {
      if (!id) return null;

      // First fetch questionnaire data to find upload type questions
      const questionnaireResponse = await fetchQuestionnaire(id, false);

      if (questionnaireResponse?.data) {
        // Find upload type question
        let uploadQuestionId: string | null = null;

        for (const moduleGroup of questionnaireResponse.data.moduleQuestions) {
          const uploadQuestion = moduleGroup.questions.find(
            (q) => q.answerType === AnswerType.Upload && q.answer,
          );

          if (uploadQuestion) {
            uploadQuestionId = uploadQuestion.id;
            break;
          }
        }

        // If found upload question with answer, get image URL
        if (uploadQuestionId) {
          const fileUrlResponse = await getQuestionnaireFileUrl(
            id,
            uploadQuestionId,
            false,
          );

          return fileUrlResponse?.data?.url || null;
        }
      }

      return null;
    },
    {
      immediate: true,
      requestKey: `result-image-${id}`,
      onSuccess: (imageUrl) => {
        if (imageUrl) {
          setUploadedImageUrl(imageUrl);
        }
      },
      onError: (error) => {
        console.error('Failed to fetch uploaded image:', error);
        // If fetch fails, use default image (don't set uploadedImageUrl)
      },
    },
  );

  const exportToPuppeteerPDF = async () => {
    try {
      await withLoading(
        async () => {
          const currentUrl = `${window.location.origin}/result/${id}`;
          const businessName =
            scoreData?.businessNameEn || scoreData?.businessName || 'SDGs';
          const filename = `${businessName}_Report_${new Date().toISOString().split('T')[0]}.pdf`;

          const pdfBlob = await exportToPDF(currentUrl, filename);

          const url = URL.createObjectURL(pdfBlob);

          // Create a temporary anchor element to trigger download
          const link = document.createElement('a');
          link.href = url;
          link.download = filename;
          link.style.display = 'none';

          // Append to body, click, and remove
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);

          // Clean up the URL after a short delay
          setTimeout(() => {
            URL.revokeObjectURL(url);
          }, 1000);
        },
        { global: true, minDuration: 500 },
      );
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('PDF 生成失敗，請確保後端服務正在運行。');
    }
  };

  // 處理加載和錯誤狀態
  if (loading && !isGenerating) {
    return (
      <div
        style={{
          backgroundColor: '#679256',
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <div style={{ color: 'white', fontSize: '18px' }}>載入中...</div>
      </div>
    );
  }

  // Handle feedback generation state
  if (isGenerating) {
    return (
      <div
        style={{
          backgroundColor: '#679256',
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <div
          style={{
            color: 'white',
            fontSize: '18px',
            textAlign: 'center',
            padding: '20px',
          }}
        >
          <div style={{ marginBottom: '20px' }}>問卷結果正在生成中...</div>
          <div style={{ fontSize: '14px', opacity: 0.9 }}>
            系統正在分析您的回答並生成個性化回饋報告，這可能需要幾分鐘時間。
          </div>
          <div style={{ fontSize: '14px', opacity: 0.9, marginTop: '10px' }}>
            請稍候，頁面將自動更新。
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div
        style={{
          backgroundColor: '#679256',
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <div style={{ color: 'white', fontSize: '18px', textAlign: 'center' }}>
          載入失敗: {error}
          <div
            style={{
              marginTop: '20px',
              display: 'flex',
              gap: '10px',
              justifyContent: 'center',
            }}
          >
            <FilledButton onClick={refetch}>重試</FilledButton>
            <FilledButton onClick={() => navigate(Path.QuestionnaireSelection)}>
              回到問卷選擇
            </FilledButton>
          </div>
        </div>
      </div>
    );
  }

  if (!resultData.length) {
    return (
      <div
        style={{
          backgroundColor: '#679256',
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <div style={{ color: 'white', fontSize: '18px' }}>無結果數據</div>
      </div>
    );
  }

  // Calculate top 3 SDGs by score
  const topThreeSdgs = scoreData?.sdgScores
    ? [...scoreData.sdgScores]
        .sort((a, b) => {
          // Sort by score descending
          if (b.score !== a.score) {
            return b.score - a.score;
          }
          // If scores are equal, prefer SDGs with more detailed descriptions
          const aHasDescription =
            a.individualFeedback && a.individualFeedback.length > 0;
          const bHasDescription =
            b.individualFeedback && b.individualFeedback.length > 0;
          if (bHasDescription && !aHasDescription) return 1;
          if (!bHasDescription && aHasDescription) return -1;
          return 0;
        })
        .slice(0, 3)
        .map((sdgScore) => {
          const sdgNumber = parseInt(sdgScore.sdgGoal.name.replace('SDG', ''));
          const sdgTitleInfo = resultData.find((r) => r.id === sdgNumber);
          return {
            sdgNumber,
            title: sdgTitleInfo?.title || `SDG ${sdgNumber}`,
            individualFeedback: sdgScore.individualFeedback || '暫無回饋資料',
            score: sdgScore.score,
          };
        })
    : [];

  // Get overall feedback or use default
  const overallFeedback =
    scoreData?.overallFeedback ||
    `綜合以上，${scoreData?.businessName || '力卡珈琲'}不僅在提供高品質的產品和服務方面做得出色，也積極參與社會責任和可持續發展的實踐。這些努力不僅增強了公司的品牌形象，也對社會和環境產生了積極的影響。`;

  return (
    <div
      style={{
        color: '#000000',
        backgroundColor: '#e6e6e6',
        maxWidth: '100vw',
        overflowX: 'hidden',
        width: '100%',
      }}
    >
      <Global styles={Style.PDFStyles} />
      {/* Floating Export Buttons - Only show when exportable */}
      {exportable && (
        <Style.FloatingExportButtons className="floating-export-buttons">
          <button onClick={exportToPuppeteerPDF} className="puppeteer-pdf">
            <span>匯出PDF</span>
          </button>
        </Style.FloatingExportButtons>
      )}

      <div id="result-content">
        <Style.Block
          className="report-title-block"
          style={{ backgroundImage: `url(${ImgLeaf})` }}
        >
          <Flex vertical flex={1}>
            {isMobileOrTablet ? (
              <>
                <Style.Text className="report-title">
                  SDGs自我分析量表
                </Style.Text>
                <Style.CustomDivider style={{ margin: 0 }} />
              </>
            ) : (
              <>
                <Style.CustomDivider style={{ margin: 0 }} />
                <Style.Text className="report-title">
                  SDGs自我分析量表
                </Style.Text>
              </>
            )}
          </Flex>
          <Flex
            vertical={!isMobileOrTablet}
            wrap
            justify={isMobileOrTablet ? 'center' : 'end'}
            align={isMobileOrTablet ? 'end' : 'start'}
            flex={!isMobileOrTablet && 1}
            gap={isMobileOrTablet && 24}
          >
            <Style.Text className="report-text">
              {scoreData?.businessNameEn ?? ''}
            </Style.Text>
            <Style.Text className="report-text">
              {scoreData?.businessName ?? ''}
            </Style.Text>
          </Flex>
        </Style.Block>

        <Style.Block className="report-description-block">
          <Style.Block
            className="about-text-block"
            $color={
              getIndustryResultAboutColors(
                scoreData?.industry?.name as Industry,
              ).color
            }
            $bgColor={
              getIndustryResultAboutColors(
                scoreData?.industry?.name as Industry,
              ).bgColor
            }
          >
            <Style.Text className="about-text title">about.</Style.Text>
            {scoreData?.businessIntroduction ? (
              <Style.Text className="about-text">
                {scoreData.businessIntroduction}
              </Style.Text>
            ) : (
              <>
                <Style.Text className="about-text">
                  {scoreData?.businessName || '力卡珈琲'}
                  是政義和Sam送給金崙居民和旅人的部落客廳，向所有人敞開雙臂，歡迎大家在金崙待久一點點，享受海灘美景之外，細心發現這個小社區的可愛和美好。
                </Style.Text>
                <Style.Text className="about-text">
                  店裡提供的民宿早餐、咖啡、甜點，都盡可能選用鄰近部落友善環境種植的當季食材。這裡的拿鐵有刺蔥和馬告的香氣，磅蛋糕裡有Vuvu釀造的小米酒糟，傳達著屬於臺東的味覺體驗和豐富層次。
                </Style.Text>
                <Style.Text className="about-text">
                  {scoreData?.businessName || '力卡珈琲'}
                  努力堅持著不使用一次性餐具的環境友善理念，如果想到海灘享受下午茶，政義會將冰咖啡裝在玻璃杯，磅蛋糕用月桃葉包好放進檳榔葉鞘，盡可能以大地素材盛裝餐飲，讓每個人都能享受貼近自然的單純喜悅。
                </Style.Text>
              </>
            )}
          </Style.Block>
          <Style.Block
            className="about-image-block"
            style={{ backgroundImage: `url(${uploadedImageUrl || ImgLika})` }}
          />
        </Style.Block>

        {/* SDG Vertical List */}
        <div>
          <Style.Block className="score-title-block">
            {isMobileOrTablet ? (
              <>
                <Style.Text className="score-title">永續指標實績</Style.Text>
                <Style.CustomDivider style={{ margin: 0 }} />
              </>
            ) : (
              <>
                <Style.CustomDivider style={{ margin: 0 }} />
                <Style.Text className="score-title">永續指標實績</Style.Text>
              </>
            )}
          </Style.Block>

          <Flex vertical>
            {resultData.map((sdg, index) => (
              <>
                <Style.Block key={sdg.id} className="sdg-item">
                  <Style.Block
                    className="sdg-content"
                    $bgImage={sdgImg[index]!}
                  >
                    <Flex vertical flex={1}>
                      <Style.Block className="sdg-title-block">
                        <Flex justify="space-between" align="center">
                          <Style.Text className="sdg-number">
                            SDG {sdg.id}
                          </Style.Text>
                          <Style.Text className="sdg-title">
                            {sdg.title}
                          </Style.Text>
                        </Flex>

                        <Style.CustomDivider style={{ margin: 0 }} />

                        <Style.Text className="sdg-description">
                          {sdg.description}
                        </Style.Text>
                      </Style.Block>
                    </Flex>

                    <CircularProgress
                      score={sdg.score}
                      sdgId={sdg.id}
                      industry={scoreData?.industry?.name as Industry}
                    />
                  </Style.Block>
                  <Style.CustomDivider
                    className={
                      ([1, 5, 9, 13].includes(index)
                        ? 'page-break-before'
                        : 'no-break') + ' sdg-separator'
                    }
                  />
                </Style.Block>
              </>
            ))}

            {!isMobileOrTablet && (
              <Style.Block className="report-logo" $bgImage={Img18} />
            )}
          </Flex>
        </div>

        <Style.Block className="charts-section">
          <Style.Block className="key-indicators-block">
            <Style.Block className="key-indicators-title-block">
              <Style.Text className="key-indicators-title">重點指標</Style.Text>
              <Style.CustomDivider style={{ margin: 0 }} />
            </Style.Block>

            <Style.Block className="key-indicators-content-block">
              <Style.Circle
                className="center"
                $color={
                  getIndustryResultCircleColors(
                    scoreData?.industry?.name as Industry,
                  ).colorCenter
                }
                $bgColor={
                  getIndustryResultCircleColors(
                    scoreData?.industry?.name as Industry,
                  ).bgCenter
                }
              >
                <Style.Block className="main-key-indicators-block">
                  <Flex align="center" vertical>
                    <Style.Text className="main-key-indicators-title business-name">
                      {scoreData?.businessName || '力卡珈琲'}
                    </Style.Text>
                    <Style.Text className="main-key-indicators-title">
                      重點指標
                    </Style.Text>
                  </Flex>
                  <Style.Text className="main-key-indicators-desc">
                    {overallFeedback}
                  </Style.Text>
                </Style.Block>
              </Style.Circle>

              {topThreeSdgs[0] && (
                <Style.Circle
                  className="left"
                  $color={
                    getIndustryResultCircleColors(
                      scoreData?.industry?.name as Industry,
                    ).colorElse
                  }
                  $bgColor={
                    getIndustryResultCircleColors(
                      scoreData?.industry?.name as Industry,
                    ).bgElse
                  }
                >
                  <Style.Block
                    className="rest-key-indicators-block left"
                    $bgImage={sdgImg[topThreeSdgs[0].sdgNumber - 1]!}
                  >
                    <Flex vertical align={isMobileOrTablet ? 'start' : 'end'}>
                      <Style.Text className="rest-key-indicators-number">
                        SDG {topThreeSdgs[0].sdgNumber}
                      </Style.Text>
                      <Style.Text className="rest-key-indicators-title">
                        {topThreeSdgs[0].title}
                      </Style.Text>
                    </Flex>
                    <Style.Text className="rest-key-indicators-desc">
                      {topThreeSdgs[0].individualFeedback}
                    </Style.Text>
                  </Style.Block>
                </Style.Circle>
              )}

              {topThreeSdgs[1] && (
                <Style.Circle
                  className="top-right"
                  $color={
                    getIndustryResultCircleColors(
                      scoreData?.industry?.name as Industry,
                    ).colorElse
                  }
                  $bgColor={
                    getIndustryResultCircleColors(
                      scoreData?.industry?.name as Industry,
                    ).bgElse
                  }
                >
                  <Style.Block
                    className="rest-key-indicators-block top-right"
                    $bgImage={sdgImg[topThreeSdgs[1].sdgNumber - 1]!}
                  >
                    <Flex vertical align={isMobileOrTablet ? 'start' : 'end'}>
                      <Style.Text className="rest-key-indicators-number">
                        SDG {topThreeSdgs[1].sdgNumber}
                      </Style.Text>
                      <Style.Text className="rest-key-indicators-title">
                        {topThreeSdgs[1].title}
                      </Style.Text>
                    </Flex>
                    <Style.Text className="rest-key-indicators-desc">
                      {topThreeSdgs[1].individualFeedback}
                    </Style.Text>
                  </Style.Block>
                </Style.Circle>
              )}

              {topThreeSdgs[2] && (
                <Style.Circle
                  className="bottom-right"
                  $color={
                    getIndustryResultCircleColors(
                      scoreData?.industry?.name as Industry,
                    ).colorElse
                  }
                  $bgColor={
                    getIndustryResultCircleColors(
                      scoreData?.industry?.name as Industry,
                    ).bgElse
                  }
                >
                  <Style.Block
                    className="rest-key-indicators-block bottom-right"
                    $bgImage={sdgImg[topThreeSdgs[2].sdgNumber - 1]!}
                  >
                    <Flex vertical align="start">
                      <Style.Text className="rest-key-indicators-number">
                        SDG {topThreeSdgs[2].sdgNumber}
                      </Style.Text>
                      <Style.Text className="rest-key-indicators-title">
                        {topThreeSdgs[2].title}
                      </Style.Text>
                    </Flex>
                    <Style.Text className="rest-key-indicators-desc">
                      {topThreeSdgs[2].individualFeedback}
                    </Style.Text>
                  </Style.Block>
                </Style.Circle>
              )}
            </Style.Block>
          </Style.Block>

          <Style.Block className="radar-chart-block">
            <Style.Block className="radar-chart-title-block">
              <Style.Text className="radar-chart-title">
                SDGs Radar Chart
              </Style.Text>
              <Style.CustomDivider style={{ margin: 0 }} />
            </Style.Block>

            <Style.Block className="radar-chart-content">
              <Flex justify="space-between" vertical={isMobileOrTablet}>
                {isMobileOrTablet ? (
                  <>
                    <Style.Block className="desc-title-block">
                      <Flex vertical>
                        <Style.Text className="desc-title">
                          {scoreData?.businessName || '力卡珈琲'}
                        </Style.Text>
                        <Style.Text className="desc-title">
                          永續落點SDGs雷達圖
                        </Style.Text>
                      </Flex>
                    </Style.Block>

                    {/* Mobile: Radar Chart first */}
                    <SDGRadarChart
                      ref={radarChartRef}
                      sdgData={resultData}
                      industry={scoreData?.industry?.name as Industry}
                      style={{ aspectRatio: 1, width: '100%', height: '90vh' }}
                    />

                    {/* Mobile: SDG List second */}
                    <Style.Block className="sdg-list-block">
                      <Flex vertical>
                        {resultData.map((sdg) => (
                          <Flex justify="start" key={sdg.id}>
                            <Style.Text className="desc number">
                              SDG {String(sdg.id).padStart(2, '0')}
                            </Style.Text>
                            <Style.Text className="desc title">
                              {sdg.title}
                            </Style.Text>
                            <Style.Text className="desc subtitle">
                              ({sdg.subtitle})
                            </Style.Text>
                          </Flex>
                        ))}
                      </Flex>
                    </Style.Block>
                  </>
                ) : (
                  <>
                    {/* Desktop: SDG List on left */}
                    <Style.Block className="sdg-list-block">
                      <Style.Block className="desc-title-block">
                        <Flex vertical>
                          <Style.Text className="desc-title">
                            {scoreData?.businessName || '力卡珈琲'}
                          </Style.Text>
                          <Style.Text className="desc-title">
                            永續落點
                          </Style.Text>
                          <Style.Text className="desc-title">
                            SDGs雷達圖
                          </Style.Text>
                        </Flex>
                      </Style.Block>

                      <Flex vertical>
                        {resultData.map((sdg) => (
                          <Flex justify="start" key={sdg.id}>
                            <Style.Text className="desc number">
                              SDG {String(sdg.id).padStart(2, '0')}
                            </Style.Text>
                            <Style.Text className="desc title">
                              {sdg.title}
                            </Style.Text>
                            <Style.Text className="desc subtitle">
                              ({sdg.subtitle})
                            </Style.Text>
                          </Flex>
                        ))}
                      </Flex>
                    </Style.Block>

                    {/* Desktop: Radar Chart on right */}
                    <SDGRadarChart
                      ref={radarChartRef}
                      sdgData={resultData}
                      industry={scoreData?.industry?.name as Industry}
                      style={{ aspectRatio: 1, width: '100%', height: '90vh' }}
                    />
                  </>
                )}
              </Flex>
            </Style.Block>
          </Style.Block>
        </Style.Block>
      </div>
    </div>
  );
}
